import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import InterpretationDetailModal from './InterpretationDetailModal';
import './parameter-selection.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDna, faEye, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const safeParseJSON = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch (error) {
    console.warn('Failed to parse JSON payload for parameter selection card:', error);
    return value;
  }
};

const toTitleCase = (raw) => {
  if (!raw || typeof raw !== 'string') {
    return raw;
  }
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b([a-z])/g, (char) => char.toUpperCase());
};

const getTierClass = (tierString) => {
  if (!tierString) return '';
  if (tierString.includes('I')) return 'tier-TIER_I';
  if (tierString.includes('II')) return 'tier-TIER_II';
  if (tierString.includes('III')) return 'tier-TIER_III';
  if (tierString.includes('IV')) return 'tier-TIER_IV_US';
  return '';
};

const InfoRow = ({ label, value }) => {
  if (!value && value !== 0) {
    return null;
  }

  return (
    <div className="param-info-row">
      <span className="param-info-label">{label}</span>
      <span className="param-info-value">{value}</span>
    </div>
  );
};

const Chip = ({ label, tone = 'neutral' }) => {
  if (!label) {
    return null;
  }

  return <span className={`param-chip param-chip-${tone}`}>{label}</span>;
};

const FILTER_OPTIONS = [
  // Consequence type filters
  { key: 'consequence:missense_variant', label: 'Missense', group: 'consequence' },
  { key: 'consequence:synonymous_variant', label: 'Synonymous', group: 'consequence' },
  { key: 'consequence:frameshift_variant', label: 'Frameshift', group: 'consequence' },
  { key: 'consequence:stop_gained', label: 'Stop Gained', group: 'consequence' },
  { key: 'consequence:splice', label: 'Splice', group: 'consequence' },
  { key: 'consequence:intergenic_variant', label: 'Intergenic', group: 'consequence' },
  // Impact filters
  { key: 'impact:HIGH', label: 'High', group: 'impact' },
  { key: 'impact:MODERATE', label: 'Moderate', group: 'impact' },
  { key: 'impact:LOW', label: 'Low', group: 'impact' },
  { key: 'impact:MODIFIER', label: 'Modifier', group: 'impact' },
  // OncoKB filters
  { key: 'oncokb:Oncogenic', label: 'Oncogenic', group: 'oncokb' },
  { key: 'oncokb:Likely Oncogenic', label: 'Likely Oncogenic', group: 'oncokb' },
  { key: 'oncokb:Resistance', label: 'Resistance', group: 'oncokb' },
  { key: 'oncokb:Unknown', label: 'Unknown', group: 'oncokb' },
  // ClinVar filters
  { key: 'clinvar:Pathogenic', label: 'Pathogenic', group: 'clinvar' },
  { key: 'clinvar:Likely pathogenic', label: 'Likely Pathogenic', group: 'clinvar' },
  { key: 'clinvar:Uncertain significance', label: 'VUS', group: 'clinvar' },
  { key: 'clinvar:Likely benign', label: 'Likely Benign', group: 'clinvar' },
  { key: 'clinvar:Benign', label: 'Benign', group: 'clinvar' },
  { key: 'clinvar:conflicting', label: 'Conflicting', group: 'clinvar' },
  // CancerVar Tier filters
  { key: 'cancervar:I', label: 'Tier I', group: 'cancervar' },
  { key: 'cancervar:II', label: 'Tier II', group: 'cancervar' },
  { key: 'cancervar:III', label: 'Tier III', group: 'cancervar' },
  { key: 'cancervar:IV', label: 'Tier IV', group: 'cancervar' },
  // AlphaMissense filters
  { key: 'alphamissense:likely_pathogenic', label: 'Likely Pathogenic', group: 'alphamissense' },
  { key: 'alphamissense:ambiguous', label: 'Ambiguous', group: 'alphamissense' },
  { key: 'alphamissense:likely_benign', label: 'Likely Benign', group: 'alphamissense' },
  // SIFT prediction filters
  { key: 'sift:deleterious', label: 'Deleterious', group: 'sift' },
  { key: 'sift:tolerated', label: 'Tolerated', group: 'sift' },
  // PolyPhen prediction filters
  { key: 'polyphen:probably_damaging', label: 'Probably Damaging', group: 'polyphen' },
  { key: 'polyphen:possibly_damaging', label: 'Possibly Damaging', group: 'polyphen' },
  { key: 'polyphen:benign', label: 'Benign', group: 'polyphen' },
  // Evidence Level filters
  { key: 'evidence:A', label: 'Level A', group: 'evidence' },
  { key: 'evidence:B', label: 'Level B', group: 'evidence' },
  { key: 'evidence:C', label: 'Level C', group: 'evidence' },
  // Database presence filters
  { key: 'database:cosmic', label: 'In COSMIC', group: 'database' },
  { key: 'database:somatic', label: 'Somatic Evidence', group: 'database' },
  // Therapeutic filters
  { key: 'therapeutic:fda', label: 'FDA Therapy', group: 'therapeutic' },
  { key: 'therapeutic:sensitivity', label: 'Drug Sensitivity', group: 'therapeutic' },
  { key: 'therapeutic:resistance', label: 'Drug Resistance', group: 'therapeutic' },
  { key: 'therapeutic:trials', label: 'Has Trials', group: 'therapeutic' },
  // Prognostic filters
  { key: 'prognostic:poor', label: 'Poor Outcome', group: 'prognostic' },
  { key: 'prognostic:good', label: 'Good Outcome', group: 'prognostic' },
];

const CHROMOSOME_OPTIONS = [
  { value: '', label: 'All Chromosomes' },
  { value: 'chr1', label: 'Chr 1' },
  { value: 'chr2', label: 'Chr 2' },
  { value: 'chr3', label: 'Chr 3' },
  { value: 'chr4', label: 'Chr 4' },
  { value: 'chr5', label: 'Chr 5' },
  { value: 'chr6', label: 'Chr 6' },
  { value: 'chr7', label: 'Chr 7' },
  { value: 'chr8', label: 'Chr 8' },
  { value: 'chr9', label: 'Chr 9' },
  { value: 'chr10', label: 'Chr 10' },
  { value: 'chr11', label: 'Chr 11' },
  { value: 'chr12', label: 'Chr 12' },
  { value: 'chr13', label: 'Chr 13' },
  { value: 'chr14', label: 'Chr 14' },
  { value: 'chr15', label: 'Chr 15' },
  { value: 'chr16', label: 'Chr 16' },
  { value: 'chr17', label: 'Chr 17' },
  { value: 'chr18', label: 'Chr 18' },
  { value: 'chr19', label: 'Chr 19' },
  { value: 'chr20', label: 'Chr 20' },
  { value: 'chr21', label: 'Chr 21' },
  { value: 'chr22', label: 'Chr 22' },
  { value: 'chrX', label: 'Chr X' },
  { value: 'chrY', label: 'Chr Y' },
  { value: 'chrM', label: 'Chr M' },
];

const VARIANT_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'SNV', label: 'SNV' },
  { value: 'insertion', label: 'Insertion' },
  { value: 'deletion', label: 'Deletion' },
  { value: 'indel', label: 'Indel' },
];

// Helper to get category data from deepQueryResult
const getCategoryData = (deepQuery, categoryName) => {
  if (!deepQuery?.tier_decision?.categories) return null;
  return deepQuery.tier_decision.categories.find(c => c.category === categoryName);
};

// Helper to get source value from Clinical Significance table
const getSourceValue = (deepQuery, sourceName) => {
  const clinSig = getCategoryData(deepQuery, 'Clinical Significance');
  if (!clinSig?.table) return null;
  const entry = clinSig.table.find(t => t.source === sourceName);
  return entry || null;
};

const getDeepQueryPayload = (row) => (row?.deepQueryResult || row?.deep_query || row?.deepQuery || null);

const getFinalTier = (row) => getDeepQueryPayload(row)?.tier_decision?.final_tier || null;

const ParamResultCard = ({ row, onViewDetails }) => {
  const deepQuery = getDeepQueryPayload(row);
  const tier = deepQuery?.tier_decision?.final_tier;

  const transcriptData = useMemo(() => {
    const parsed = safeParseJSON(row.transcript_consequences);
    return Array.isArray(parsed) ? parsed : [];
  }, [row.transcript_consequences]);

  const canonicalTranscript = useMemo(() => {
    if (!transcriptData.length) {
      return null;
    }
    return (
      transcriptData.find((item) => String(item?.canonical) === '1') ||
      transcriptData[0]
    );
  }, [transcriptData]);

  const geneSymbol =
    row.gene_symbol ||
    row.geneSymbol ||
    canonicalTranscript?.gene_symbol ||
    deepQuery?.decision_support?.variant_identity?.gene;

  const aminoAcids = canonicalTranscript?.amino_acids;
  const proteinPosition = canonicalTranscript?.protein_start || canonicalTranscript?.protein_end;
  const fallbackProteinChange = (() => {
    if (!aminoAcids || !proteinPosition) {
      return null;
    }
    const parts = aminoAcids.split('/');
    if (parts.length === 2) {
      return `p.${parts[0]}${proteinPosition}${parts[1]}`;
    }
    return `p.${aminoAcids}${proteinPosition}`;
  })();

  const proteinChange =
    canonicalTranscript?.hgvsp ||
    deepQuery?.decision_support?.variant_identity?.full_hgvs ||
    fallbackProteinChange;

  const cDnaChange = canonicalTranscript?.hgvsc;

  const rsId =
    (row.id && row.id !== '.' && row.id.toUpperCase()) ||
    deepQuery?.decision_support?.variant_identity?.rs_id;

  const genomicLocation =
    canonicalTranscript?.hgvsg ||
    deepQuery?.decision_support?.variant_identity?.genomic_location ||
    (row.seq_region_name && row.start ? `${row.seq_region_name}:${row.start}` : null);

  const variantClass =
    row.variant_class ||
    deepQuery?.after_enrichment?.variant_class ||
    toTitleCase(row.variantType);

  const overallScore = deepQuery?.tier_decision?.overall_score;
  const formattedOverallScore =
    typeof overallScore === 'number'
      ? overallScore.toFixed(2)
      : overallScore;

  const consequenceChips = useMemo(() => {
    if (Array.isArray(canonicalTranscript?.consequence_terms)) {
      return canonicalTranscript.consequence_terms.map((term) => toTitleCase(term));
    }
    return row.most_severe_consequence
      ? [toTitleCase(row.most_severe_consequence)]
      : [];
  }, [canonicalTranscript, row.most_severe_consequence]);

  const classificationChips = useMemo(() => {
    if (!deepQuery?.decision_support?.clinical_significance) {
      return [];
    }
    return deepQuery.decision_support.clinical_significance.map((item) => ({
      key: `${item.source}-${item.value}`,
      label: `${item.source}: ${item.value}`,
      tone: item.source === 'OncoKB' ? 'accent' : 'outline',
    }));
  }, [deepQuery]);

  const flagBadges = useMemo(() => {
    const flags = deepQuery?.tier_decision?.flags;
    if (!flags) {
      return [];
    }
    return Object.entries(flags)
      .filter(([, value]) => Boolean(value))
      .map(([key]) => ({
        key,
        label: toTitleCase(key),
      }));
  }, [deepQuery]);

  const therapiesList = useMemo(() => {
    const entries = new Map();

    const addEntry = (payload) => {
      if (!payload) {
        return;
      }
      const key = `${payload.drugs || payload.name || 'therapy'}-${payload.cancerType || payload.disease || 'na'}`;
      if (!entries.has(key)) {
        entries.set(key, payload);
      }
    };

    const therapeuticCategory = deepQuery?.tier_decision?.categories?.find(
      (category) => category.category === 'Therapeutic Actionability'
    );

    therapeuticCategory?.fda_therapies?.forEach((therapy) => {
      addEntry({
        drugs: therapy.drugs,
        cancerType: therapy.cancerType,
        evidenceLevel: therapy.evidenceLevel || therapy.level,
      });
    });

    deepQuery?.after_enrichment?.custom_annotations?.OncoKB?.topTierTreatments?.forEach(
      (therapy) => {
        addEntry({
          drugs: therapy.drugs,
          cancerType: therapy.cancerType,
          evidenceLevel: therapy.evidenceLevel,
        });
      }
    );

    return Array.from(entries.values()).slice(0, 3);
  }, [deepQuery]);

  const trialList = useMemo(() => {
    const trials = deepQuery?.decision_support?.clinical_trials?.active_trials;
    if (!Array.isArray(trials)) {
      return [];
    }
    return trials.slice(0, 3);
  }, [deepQuery]);

  const predictionMetrics = useMemo(() => {
    const scores = deepQuery?.decision_support?.prediction_scores;
    if (!scores) {
      return [];
    }
    return Object.entries(scores)
      .filter(([, value]) => Boolean(value))
      .map(([key, value]) => ({
        key,
        label: toTitleCase(key),
        value,
      }));
  }, [deepQuery]);

  const summaryText = deepQuery?.tier_decision?.summary;
  const oncokbSummary = deepQuery?.after_enrichment?.custom_annotations?.OncoKB?.summary?.variantSummary;

  return (
    <div className="param-result-tile">
      <div className="param-result-header">
<div style={{ display: "flex" }}>
  <span className="param-result-type param-result-type-biomarker">
    <FontAwesomeIcon icon={faDna} /> BIOMARKER
  </span>
</div>

        {deepQuery ? (
          <button className="ocpr-view-details-btn" onClick={onViewDetails}>
            <FontAwesomeIcon icon={faInfoCircle} />
            <span >
              {tier || 'Details'}
            </span>
          </button>
        ) : (
          <span className="ocpr-no-details">No deep dive data</span>
        )}
      </div>

      <div className="param-card-main">
        <div className="param-variant-headline">
          <span className="param-variant-gene">{geneSymbol || 'Unknown Gene'}</span>
          {proteinChange && (
            <span className="param-variant-protein">{proteinChange}</span>
          )}
        </div>

        <div className="param-chip-row">
          {rsId && <Chip label={rsId} tone="accent" />}
          {variantClass && <Chip label={toTitleCase(variantClass)} tone="outline" />}
          {consequenceChips.map((chip) => (
            <Chip key={chip} label={chip} tone="neutral" />
          ))}
        </div>

        <div className="param-info-grid">
          <InfoRow label="Genomic Location" value={genomicLocation} />
          <InfoRow label="Transcript" value={canonicalTranscript?.transcript_id} />
          <InfoRow label="cDNA Change" value={cDnaChange} />
          <InfoRow label="Impact Score" value={formattedOverallScore} />
        </div>

        {classificationChips.length > 0 && (
          <div className="param-card-section">
            <div className="param-section-title">Clinical Significance</div>
            <div className="param-chip-row">
              {classificationChips.map((chip) => (
                <Chip key={chip.key} label={chip.label} tone={chip.tone} />
              ))}
            </div>
          </div>
        )}

        {flagBadges.length > 0 && (
          <div className="param-card-section">
            <div className="param-section-title">Highlights</div>
            <div className="param-chip-row">
              {flagBadges.map((flag) => (
                <Chip key={flag.key} label={flag.label} tone="success" />
              ))}
            </div>
          </div>
        )}

        {summaryText && (
          <div className="param-card-section">
            <div className="param-section-title">Tier Summary</div>
            <p className="param-section-text">{summaryText}</p>
          </div>
        )}

        {oncokbSummary && (
          <div className="param-card-section">
            <div className="param-section-title">OncoKB Insight</div>
            <p className="param-section-text">{oncokbSummary}</p>
          </div>
        )}

        {therapiesList.length > 0 && (
          <div className="param-card-section">
            <div className="param-section-title">Therapies</div>
            <ul className="param-section-list">
              {therapiesList.map((therapy, index) => (
                <li key={`${therapy.drugs}-${index}`}>
                  <span className="param-list-primary">{therapy.drugs}</span>
                  {therapy.cancerType && (
                    <span className="param-list-secondary">{therapy.cancerType}</span>
                  )}
                  {therapy.evidenceLevel && (
                    <span className="param-list-tag">{therapy.evidenceLevel}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {trialList.length > 0 && (
          <div className="param-card-section">
            <div className="param-section-title">Clinical Trials</div>
            <ul className="param-section-list">
              {trialList.map((trial) => (
                <li key={trial.nctId || trial.nct_id || trial.title}>
                  <span className="param-list-primary">{trial.title}</span>
                  <span className="param-list-secondary">
                    {[trial.phase, trial.status, trial.nctId || trial.nct_id]
                      .filter(Boolean)
                      .join(' � ')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {predictionMetrics.length > 0 && (
          <div className="param-card-section">
            <div className="param-section-title">Computational Predictions</div>
            <div className="param-metric-row">
              {predictionMetrics.map((metric) => (
                <div className="param-metric" key={metric.key}>
                  <span className="param-metric-label">{metric.label}</span>
                  <span className="param-metric-value">{metric.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function ParameterSelection({
  selectedJob,
  resultsData,
  resultsSearch,
  setResultsSearch,
}) {
  const [modalData, setModalData] = useState(null);
  const [filteredResults, setFilteredResults] = useState([]);
  const [displayedResults, setDisplayedResults] = useState([]);
  const [visibleCount, setVisibleCount] = useState(20);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [chromosomeFilter, setChromosomeFilter] = useState('');
  const [variantTypeFilter, setVariantTypeFilter] = useState('');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef(null);
  const filterButtonRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const resultsContainerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is inside the button container
      if (filterDropdownRef.current && filterDropdownRef.current.contains(e.target)) {
        return;
      }
      // Check if click is inside the portal dropdown
      if (e.target.closest('.param-filter-portal')) {
        return;
      }
      setFilterDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleFilter = (key) => {
    setSelectedFilters((prev) => {
      // Toggle filter on/off
      if (prev.includes(key)) {
        const newFilters = prev.filter(f => f !== key);
        return newFilters;
      }
      return [...prev, key];
    });
  };

  useEffect(() => {
    if (resultsData && resultsData.length > 0) {
      console.log(
        "%c--- ParameterSelection Component Received Data ---",
        'color: #ffc107; font-weight: bold;'
      );
      console.log(`Total variants received: ${resultsData.length}`);

      const variantsWithDeepQuery = resultsData.filter((row) => !!getDeepQueryPayload(row));

      if (variantsWithDeepQuery.length > 0) {
        console.log(
          `%cFound ${variantsWithDeepQuery.length} variants with 'deepQueryResult' data.`,
          'color: #28a745;'
        );
        console.log('Example of a row WITH deep query data attached:', variantsWithDeepQuery[0]);
      } else {
        console.warn(
          "WARNING: No variants in the received data have the 'deepQueryResult' property attached."
        );
      }
    }
  }, [resultsData]);

  useEffect(() => {
    if (!resultsData) {
      setFilteredResults([]);
      return;
    }

    // Group filters by type
    const consequenceFilters = selectedFilters.filter(f => f.startsWith('consequence:')).map(f => f.replace('consequence:', ''));
    const impactFilters = selectedFilters.filter(f => f.startsWith('impact:')).map(f => f.replace('impact:', ''));
    const siftFilters = selectedFilters.filter(f => f.startsWith('sift:')).map(f => f.replace('sift:', ''));
    const polyphenFilters = selectedFilters.filter(f => f.startsWith('polyphen:')).map(f => f.replace('polyphen:', ''));
    const oncokbFilters = selectedFilters.filter(f => f.startsWith('oncokb:')).map(f => f.replace('oncokb:', ''));
    const clinvarFilters = selectedFilters.filter(f => f.startsWith('clinvar:')).map(f => f.replace('clinvar:', ''));
    const cancervarFilters = selectedFilters.filter(f => f.startsWith('cancervar:')).map(f => f.replace('cancervar:', ''));
    const alphamissenseFilters = selectedFilters.filter(f => f.startsWith('alphamissense:')).map(f => f.replace('alphamissense:', ''));
    const evidenceFilters = selectedFilters.filter(f => f.startsWith('evidence:')).map(f => f.replace('evidence:', ''));
    const databaseFilters = selectedFilters.filter(f => f.startsWith('database:')).map(f => f.replace('database:', ''));
    const therapeuticFilters = selectedFilters.filter(f => f.startsWith('therapeutic:')).map(f => f.replace('therapeutic:', ''));
    const prognosticFilters = selectedFilters.filter(f => f.startsWith('prognostic:')).map(f => f.replace('prognostic:', ''));

    const filtered = resultsData.filter((row) => {
      const query = resultsSearch.toLowerCase();
      const deepQuery = getDeepQueryPayload(row);
      const transcriptArray = safeParseJSON(row.transcript_consequences);
      const primaryTranscript = Array.isArray(transcriptArray)
        ? transcriptArray.find((item) => String(item?.canonical) === '1') || transcriptArray[0]
        : null;

      // Search query filter
      if (query) {
        const matchesQuery =
          primaryTranscript?.gene_symbol?.toLowerCase().includes(query) ||
          row.most_severe_consequence?.toLowerCase().includes(query) ||
          primaryTranscript?.hgvsp?.toLowerCase().includes(query) ||
          primaryTranscript?.hgvsc?.toLowerCase().includes(query) ||
          (row.id && row.id !== '.' && row.id?.toLowerCase().includes(query)) ||
          (row.row_id && String(row.row_id).toLowerCase().includes(query));

        if (!matchesQuery) {
          return false;
        }
      }

      // Chromosome filter (dropdown)
      if (chromosomeFilter) {
        const chr = row.seq_region_name || '';
        if (chr !== chromosomeFilter) {
          return false;
        }
      }

      // Variant type filter (dropdown)
      if (variantTypeFilter) {
        const variantClass = row.variant_class || '';
        if (variantClass !== variantTypeFilter) {
          return false;
        }
      }

      // Consequence type filter
      if (consequenceFilters.length > 0) {
        const consequence = row.most_severe_consequence || '';
        const matchesConsequence = consequenceFilters.some(cf => {
          if (cf === 'splice') return consequence.includes('splice');
          return consequence === cf;
        });
        if (!matchesConsequence) return false;
      }

      // Impact filter
      if (impactFilters.length > 0) {
        const impact = primaryTranscript?.impact || '';
        if (!impactFilters.includes(impact)) return false;
      }

      // SIFT prediction filter
      if (siftFilters.length > 0) {
        const sift = primaryTranscript?.sift_prediction || '';
        if (!siftFilters.includes(sift)) return false;
      }

      // PolyPhen prediction filter
      if (polyphenFilters.length > 0) {
        const polyphen = primaryTranscript?.polyphen_prediction || '';
        if (!polyphenFilters.includes(polyphen)) return false;
      }

      // OncoKB filter (from deepQueryResult Clinical Significance)
      if (oncokbFilters.length > 0) {
        const oncokbEntry = getSourceValue(deepQuery, 'OncoKB');
        if (!oncokbEntry || !oncokbFilters.some(f => oncokbEntry.value?.includes(f))) return false;
      }

      // ClinVar filter (from deepQueryResult Clinical Significance)
      if (clinvarFilters.length > 0) {
        const clinvarEntry = getSourceValue(deepQuery, 'ClinVar');
        if (!clinvarEntry) return false;
        const matchesClinvar = clinvarFilters.some(f => {
          if (f === 'conflicting') return clinvarEntry.conflicting === true;
          return clinvarEntry.value?.toLowerCase().includes(f.toLowerCase());
        });
        if (!matchesClinvar) return false;
      }

      // CancerVar tier filter
      if (cancervarFilters.length > 0) {
        const cancervarEntry = getSourceValue(deepQuery, 'CancerVar');
        if (!cancervarEntry || !cancervarFilters.some(f => cancervarEntry.tier === f)) return false;
      }

      // AlphaMissense filter
      if (alphamissenseFilters.length > 0) {
        const amClass = primaryTranscript?.alphamissense?.am_class || '';
        // Also check deepQueryResult
        const amEntry = getSourceValue(deepQuery, 'AlphaMissense');
        const amValue = amEntry?.value?.toLowerCase().replace(' ', '_') || amClass;
        if (!alphamissenseFilters.some(f => amValue.includes(f))) return false;
      }

      // Evidence level filter
      if (evidenceFilters.length > 0) {
        const evidenceCategory = getCategoryData(deepQuery, 'Evidence Strength');
        const levelCounts = evidenceCategory?.level_counts || {};
        const hasEvidence = evidenceFilters.some(level => (levelCounts[level] || 0) > 0);
        if (!hasEvidence) return false;
      }

      // Database presence filter (COSMIC, somatic)
      if (databaseFilters.length > 0) {
        const popCategory = getCategoryData(deepQuery, 'Population Rarity');
        const popTable = popCategory?.population_table?.[0] || {};
        const matchesDatabase = databaseFilters.every(db => {
          if (db === 'cosmic') return popTable.cosmic_present === 'Yes';
          if (db === 'somatic') return popTable.somatic_evidence === 'Yes';
          return true;
        });
        if (!matchesDatabase) return false;
      }

      // Therapeutic filter
      if (therapeuticFilters.length > 0) {
        const therapCategory = getCategoryData(deepQuery, 'Therapeutic Actionability');
        const trialCategory = getCategoryData(deepQuery, 'Clinical Trial Landscape');
        const matchesTherapeutic = therapeuticFilters.every(t => {
          if (t === 'fda') return (therapCategory?.fda_therapies?.length || 0) > 0;
          if (t === 'sensitivity') return (therapCategory?.civic_sensitivity?.length || 0) > 0;
          if (t === 'resistance') return (therapCategory?.civic_resistance?.length || 0) > 0;
          if (t === 'trials') return (trialCategory?.trials?.length || 0) > 0;
          return true;
        });
        if (!matchesTherapeutic) return false;
      }

      // Prognostic filter
      if (prognosticFilters.length > 0) {
        const progCategory = getCategoryData(deepQuery, 'Prognostic Impact');
        const prognosticData = progCategory?.civic_prognostic || [];
        const matchesPrognostic = prognosticFilters.some(p => {
          if (p === 'poor') return prognosticData.some(pd => pd.type === 'POOR_OUTCOME');
          if (p === 'good') return prognosticData.some(pd => pd.type === 'GOOD_OUTCOME');
          return false;
        });
        if (!matchesPrognostic) return false;
      }

      return true;
    });

    const sorted = filtered.sort((a, b) => {
      const aHasData = !!getDeepQueryPayload(a);
      const bHasData = !!getDeepQueryPayload(b);
      if (aHasData && !bHasData) return -1;
      if (!aHasData && bHasData) return 1;

      if (aHasData && bHasData) {
        const tierA = getFinalTier(a) || 'TIER Z';
        const tierB = getFinalTier(b) || 'TIER Z';
        if (tierA < tierB) return -1;
        if (tierA > tierB) return 1;
      }
      return 0;
    });

    setFilteredResults(sorted);
    setVisibleCount(20);
  }, [resultsData, resultsSearch, selectedFilters, chromosomeFilter, variantTypeFilter]);

  useEffect(() => {
    setDisplayedResults(filteredResults.slice(0, visibleCount));
  }, [filteredResults, visibleCount]);

  const summaryMetrics = useMemo(() => {
    const total = Array.isArray(resultsData) ? resultsData.length : 0;
    const filteredTotal = filteredResults.length;
    const showing = displayedResults.length;

    const tierCountsMap = new Map();

    filteredResults.forEach((row) => {
      const tier = getFinalTier(row) || 'Unclassified';
      tierCountsMap.set(tier, (tierCountsMap.get(tier) || 0) + 1);
    });

    const tierSummaries = [];
    const orderedTierKeys = ['TIER I', 'TIER II', 'TIER III', 'TIER IV'];

    orderedTierKeys.forEach((key) => {
      if (tierCountsMap.has(key)) {
        tierSummaries.push({ label: key, count: tierCountsMap.get(key) });
        tierCountsMap.delete(key);
      }
    });

    tierCountsMap.forEach((count, key) => {
      if (key !== 'Unclassified') {
        tierSummaries.push({ label: key, count });
      }
    });

    return {
      total,
      filteredTotal,
      showing,
      tierSummaries,
    };
  }, [resultsData, filteredResults, displayedResults]);


  const handleScroll = useCallback(() => {
    const container = resultsContainerRef.current;
    if (container) {
      const isNearBottom =
        container.scrollTop + container.clientHeight >= container.scrollHeight - 200;
      if (isNearBottom && visibleCount < filteredResults.length) {
        setVisibleCount((prev) => prev + 20);
      }
    }
  }, [visibleCount, filteredResults.length]);

  useEffect(() => {
    const container = resultsContainerRef.current;
    container?.addEventListener('scroll', handleScroll);
    return () => container?.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleViewDetailsClick = (row) => {
    const deepQuery = getDeepQueryPayload(row);
    setModalData({
      baseVariant: row,
      deepQuery,
    });
  };

  return (
    <div className="param-container">
      {resultsData && resultsData.length > 0 ? (
        <>
          <div className="param-controls">
            <div className="param-search-wrapper">
              <input
                type="text"
                placeholder="Search Gene, rsID, HGVS..."
                value={resultsSearch}
                onChange={(e) => setResultsSearch(e.target.value)}
                className="param-search-input"
              />
              <i className="fas fa-search param-search-icon"></i>
            </div>
            <div className="param-filter-dropdown" ref={filterDropdownRef}>
              <button
                ref={filterButtonRef}
                type="button"
                className="param-filter-dropdown-toggle"
                onClick={() => {
                  if (!filterDropdownOpen && filterButtonRef.current) {
                    const rect = filterButtonRef.current.getBoundingClientRect();
                    setDropdownPosition({
                      top: rect.bottom + 8,
                      right: window.innerWidth - rect.right
                    });
                  }
                  setFilterDropdownOpen(!filterDropdownOpen);
                }}
              >
                <i className="fas fa-filter"></i>
                <span>Filters</span>
                {(selectedFilters.length > 0 || chromosomeFilter || variantTypeFilter) && (
                  <span className="param-filter-count">{selectedFilters.length + (chromosomeFilter ? 1 : 0) + (variantTypeFilter ? 1 : 0)}</span>
                )}
                <i className={`fas fa-chevron-${filterDropdownOpen ? 'up' : 'down'}`}></i>
              </button>
              {filterDropdownOpen && ReactDOM.createPortal(
                <div className="param-filter-dropdown-menu param-filter-portal" style={{ top: dropdownPosition.top, right: dropdownPosition.right }}>
                  {/* Dropdown filters */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">Chromosome</div>
                    <select className="param-filter-select" value={chromosomeFilter} onChange={(e) => setChromosomeFilter(e.target.value)}>
                      {CHROMOSOME_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </select>
                  </div>
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">Variant Type</div>
                    <select className="param-filter-select" value={variantTypeFilter} onChange={(e) => setVariantTypeFilter(e.target.value)}>
                      {VARIANT_TYPE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </select>
                  </div>

                  {/* Consequence */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">Consequence</div>
                    {FILTER_OPTIONS.filter(o => o.group === 'consequence').map((option) => (
                      <label key={option.key} className="param-filter-dropdown-item">
                        <input type="checkbox" checked={selectedFilters.includes(option.key)} onChange={() => toggleFilter(option.key)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Impact */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">Impact</div>
                    {FILTER_OPTIONS.filter(o => o.group === 'impact').map((option) => (
                      <label key={option.key} className="param-filter-dropdown-item">
                        <input type="checkbox" checked={selectedFilters.includes(option.key)} onChange={() => toggleFilter(option.key)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* OncoKB */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">OncoKB</div>
                    {FILTER_OPTIONS.filter(o => o.group === 'oncokb').map((option) => (
                      <label key={option.key} className="param-filter-dropdown-item">
                        <input type="checkbox" checked={selectedFilters.includes(option.key)} onChange={() => toggleFilter(option.key)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* ClinVar */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">ClinVar</div>
                    {FILTER_OPTIONS.filter(o => o.group === 'clinvar').map((option) => (
                      <label key={option.key} className="param-filter-dropdown-item">
                        <input type="checkbox" checked={selectedFilters.includes(option.key)} onChange={() => toggleFilter(option.key)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* CancerVar */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">CancerVar</div>
                    {FILTER_OPTIONS.filter(o => o.group === 'cancervar').map((option) => (
                      <label key={option.key} className="param-filter-dropdown-item">
                        <input type="checkbox" checked={selectedFilters.includes(option.key)} onChange={() => toggleFilter(option.key)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* AlphaMissense */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">AlphaMissense</div>
                    {FILTER_OPTIONS.filter(o => o.group === 'alphamissense').map((option) => (
                      <label key={option.key} className="param-filter-dropdown-item">
                        <input type="checkbox" checked={selectedFilters.includes(option.key)} onChange={() => toggleFilter(option.key)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* SIFT */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">SIFT</div>
                    {FILTER_OPTIONS.filter(o => o.group === 'sift').map((option) => (
                      <label key={option.key} className="param-filter-dropdown-item">
                        <input type="checkbox" checked={selectedFilters.includes(option.key)} onChange={() => toggleFilter(option.key)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* PolyPhen */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">PolyPhen</div>
                    {FILTER_OPTIONS.filter(o => o.group === 'polyphen').map((option) => (
                      <label key={option.key} className="param-filter-dropdown-item">
                        <input type="checkbox" checked={selectedFilters.includes(option.key)} onChange={() => toggleFilter(option.key)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Evidence Level */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">Evidence Level</div>
                    {FILTER_OPTIONS.filter(o => o.group === 'evidence').map((option) => (
                      <label key={option.key} className="param-filter-dropdown-item">
                        <input type="checkbox" checked={selectedFilters.includes(option.key)} onChange={() => toggleFilter(option.key)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Database */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">Database</div>
                    {FILTER_OPTIONS.filter(o => o.group === 'database').map((option) => (
                      <label key={option.key} className="param-filter-dropdown-item">
                        <input type="checkbox" checked={selectedFilters.includes(option.key)} onChange={() => toggleFilter(option.key)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Therapeutic */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">Therapeutic</div>
                    {FILTER_OPTIONS.filter(o => o.group === 'therapeutic').map((option) => (
                      <label key={option.key} className="param-filter-dropdown-item">
                        <input type="checkbox" checked={selectedFilters.includes(option.key)} onChange={() => toggleFilter(option.key)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Prognostic */}
                  <div className="param-filter-dropdown-section">
                    <div className="param-filter-dropdown-label">Prognostic</div>
                    {FILTER_OPTIONS.filter(o => o.group === 'prognostic').map((option) => (
                      <label key={option.key} className="param-filter-dropdown-item">
                        <input type="checkbox" checked={selectedFilters.includes(option.key)} onChange={() => toggleFilter(option.key)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>,
                document.body
              )}
            </div>
          </div>

          <div className="param-summary-bar">
            <div className="param-summary-primary">
              Showing {summaryMetrics.showing} of {summaryMetrics.filteredTotal} variants
              {summaryMetrics.total !== summaryMetrics.filteredTotal && (
                <span className="param-summary-total"> ({summaryMetrics.total} total)</span>
              )}
            </div>
            {summaryMetrics.tierSummaries.length > 0 && (
              <div className="param-summary-tiers">
                {summaryMetrics.tierSummaries.map((tier) => (
                  <span key={`${tier.label}-${tier.count}`} className="param-summary-tier">
                    {tier.label}: {tier.count}
                  </span>
                ))}
              </div>
            )}
          </div>          <div className="param-results-list" ref={resultsContainerRef}>
            {displayedResults.length > 0 ? (
              <div className="param-results-grid">
                {displayedResults.map((row) => (
                  <ParamResultCard
                    key={row.row_id || row.id || row.variant_id}
                    row={row}
                    onViewDetails={() => handleViewDetailsClick(row)}
                  />
                ))}
              </div>
            ) : (
              <div className="param-no-results">
                <i className="fas fa-search"></i>
                <p>No results match your search criteria</p>
              </div>
            )}
            {visibleCount < filteredResults.length && (
              <div className="param-load-more">
                <div className="param-loading-indicator">
                  <div className="param-loading-dot"></div>
                  <div className="param-loading-dot"></div>
                  <div className="param-loading-dot"></div>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="param-placeholder">
          <i className="fas fa-clipboard-list"></i>
          <p>
            {selectedJob
              ? 'No results data available for this job'
              : 'Select a job to view parameters'}
          </p>
        </div>
      )}

      <InterpretationDetailModal
        isOpen={!!modalData}
        onClose={() => setModalData(null)}
        interpretationData={modalData}
      />
    </div>
  );
}

export default ParameterSelection;












